<template>
  <transition name="slide-down">
    <div v-if="showUpdate" class="update-notification">
      <div class="update-content">
        <div class="update-icon">
          <i class="fas fa-download"></i>
        </div>
        <div class="update-text">
          <strong>Update Available!</strong>
          <p>A new version of ClockWork is ready to install.</p>
        </div>
        <div class="update-actions">
          <button @click="applyUpdate" class="btn btn-primary btn-sm me-2"><i class="fas fa-sync-alt"></i> Update Now</button>
          <button @click="dismissUpdate" class="btn btn-outline-secondary btn-sm">Later</button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { ref, onMounted, onUnmounted } from "vue";

export default {
  name: "cUpdateNotification",
  setup() {
    const showUpdate = ref(false);
    let updateHandler = null;

    const handleUpdateAvailable = (event) => {
      console.log("[Component] Update notification received:", event);
      console.log("[Component] Event detail:", event.detail);
      console.log("[Component] Current showUpdate value:", showUpdate.value);
      showUpdate.value = true;
      updateHandler = event.detail;
      console.log("[Component] showUpdate set to:", showUpdate.value);
      console.log("[Component] updateHandler set to:", updateHandler);
    };

    const applyUpdate = () => {
      console.log("Apply update clicked");

      // Hide the banner immediately
      showUpdate.value = false;

      // Show a loading message
      if (window.store) {
        window.store.dispatch("showToast", {
          message: "Updating app...",
          type: "info",
          duration: 2000,
        });
      }

      if (updateHandler && updateHandler.skipWaiting) {
        console.log("Calling skipWaiting...");
        updateHandler.skipWaiting();
      } else {
        console.log("No update handler available, forcing reload");
        // Fallback: just reload the page
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    };

    const dismissUpdate = () => {
      console.log("Update dismissed by user");
      showUpdate.value = false;
      sessionStorage.setItem("cw.vue.updatePrompted", "true");
      // Don't remove the newVersion flag - keep it for next navigation
    };

    onMounted(() => {
      console.log("[Component] cUpdateNotification component mounted");

      // Make component testable from console
      window.testUpdateNotification = () => {
        console.log("[Component] Manual test triggered");
        showUpdate.value = true;
        updateHandler = { skipWaiting: () => console.log("Test skip waiting") };
      };

      // Check if we just completed an update
      const updateInProgress = sessionStorage.getItem("cw.vue.updateInProgress") === "true";
      console.log("[Component] Update in progress flag:", updateInProgress);
      if (updateInProgress) {
        console.log("[Component] Update was just applied, clearing flags and not showing banner");
        sessionStorage.removeItem("cw.vue.updateInProgress");
        sessionStorage.removeItem("cw.vue.newVersion");
        sessionStorage.removeItem("cw.vue.updatePrompted");
        return;
      }

      // Listen for update available events
      window.addEventListener("sw-update-available", handleUpdateAvailable);
      console.log("[Component] Event listener added for sw-update-available");

      // Check if we should show update on mount
      const hasUpdate = sessionStorage.getItem("cw.vue.newVersion") === "true";
      const wasPrompted = sessionStorage.getItem("cw.vue.updatePrompted") === "true";
      console.log("[Component] Mount check - hasUpdate:", hasUpdate, "wasPrompted:", wasPrompted);

      if (hasUpdate && !wasPrompted) {
        console.log("[Component] Showing update on mount");
        showUpdate.value = true;
      }
    });

    onUnmounted(() => {
      window.removeEventListener("sw-update-available", handleUpdateAvailable);
    });

    return {
      showUpdate,
      applyUpdate,
      dismissUpdate,
    };
  },
};
</script>

<style scoped>
.update-notification {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, #4caf50, #45a049);
  color: white;
  z-index: 99999;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.update-content {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.update-icon {
  font-size: 24px;
  margin-right: 15px;
  animation: pulse 2s infinite;
}

.update-text {
  flex: 1;
  margin-right: 20px;
}

.update-text p {
  margin: 0;
  opacity: 0.9;
  font-size: 14px;
}

.update-actions {
  display: flex;
  align-items: center;
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: transform 0.3s ease-out;
}

.slide-down-enter-from {
  transform: translateY(-100%);
}

.slide-down-leave-to {
  transform: translateY(-100%);
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
  100% {
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .update-content {
    flex-direction: column;
    text-align: center;
    padding: 15px;
  }

  .update-icon {
    margin-right: 0;
    margin-bottom: 10px;
  }

  .update-text {
    margin-right: 0;
    margin-bottom: 15px;
  }
}
</style>
